import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsAPI } from '../api';
import { useTheme } from '../ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import Sidebar from '../components/Sidebar';
import { ArrowLeft, Search, Upload, Download, Edit2, Trash2, User, Camera, Grid, List, Clock, Send, MessageCircle, Share2 } from 'lucide-react';
import './Events.css';

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all'); // all, photographer, attendee, pending
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editRole, setEditRole] = useState('attendee');
  const [saving, setSaving] = useState(false);
  const [event, setEvent] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Load saved view mode from localStorage, default to 'card'
    return localStorage.getItem('manageUsersViewMode') || 'card';
  });
  const [showAddAttendeeModal, setShowAddAttendeeModal] = useState(false);
  const [newAttendeeName, setNewAttendeeName] = useState('');
  const [newAttendeeEmail, setNewAttendeeEmail] = useState('');
  const [newAttendeePhone, setNewAttendeePhone] = useState('');
  const [addingAttendee, setAddingAttendee] = useState(false);
  const [previousPendingCount, setPreviousPendingCount] = useState(0);
  const [sendingGalleryLink, setSendingGalleryLink] = useState({});
  const [sendingFullGallery, setSendingFullGallery] = useState({});
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null); // null = broadcast, or user object
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Bulk delete states
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Send Gallery modal states
  const [showSendGalleryModal, setShowSendGalleryModal] = useState(false);
  const [sendGalleryType, setSendGalleryType] = useState('personal'); // 'personal' or 'full'
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [sendingGallery, setSendingGallery] = useState(false);
  
  // Individual row Send Gallery modal
  const [showRowGalleryModal, setShowRowGalleryModal] = useState(false);
  const [rowGalleryUser, setRowGalleryUser] = useState(null);
  const [rowGalleryType, setRowGalleryType] = useState('personal');
  
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const logoUrl = theme === 'light' 
    ? 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7ade8283bc1015dd30.webp'
    : 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  useEffect(() => {
    loadUsers();
    loadEvent();
    
    // Auto-refresh every 5 seconds (silent mode - no loading spinner)
    const interval = setInterval(() => {
      loadUsers(true);
    }, 5000);
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('manageUsersViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    filterUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, roleFilter, searchQuery]);

  // Browser notification for new photographer requests
  useEffect(() => {
    const pendingCount = users.filter(u => u.photographer_requested && u.role === 'attendee').length;
    
    // If pending count increased, show browser notification
    if (previousPendingCount > 0 && pendingCount > previousPendingCount) {
      const newRequests = pendingCount - previousPendingCount;
      
      // Request notification permission if not granted
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
      
      // Show browser notification
      if (Notification.permission === 'granted') {
        new Notification('New Photographer Request', {
          body: `${newRequests} new photographer request${newRequests > 1 ? 's' : ''} for ${event?.name || 'your event'}`,
          icon: logoUrl,
          badge: logoUrl,
          tag: 'photographer-request',
          requireInteraction: true
        });
      }
    }
    
    setPreviousPendingCount(pendingCount);
  }, [users, previousPendingCount, event, logoUrl]);

  const loadEvent = async () => {
    try {
      const response = await eventsAPI.getOne(eventId);
      setEvent(response.data);
    } catch (error) {
      console.error('Error loading event:', error);
    }
  };

  const loadUsers = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await eventsAPI.getUsers(eventId);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      if (!silent) alert('Failed to load users');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;
    
    // Role filter
    if (roleFilter === 'pending') {
      filtered = filtered.filter(u => u.photographer_requested && u.role === 'attendee');
    } else if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query) ||
        u.phone?.toLowerCase().includes(query)
      );
    }
    
    setFilteredUsers(filtered);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditName(user.name || '');
    setEditEmail(user.email || '');
    setEditPhone(user.phone || '');
    setEditRole(user.role || 'attendee');
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      await eventsAPI.updateUser(editingUser.id, {
        name: editName,
        email: editEmail,
        phone: editPhone,
        role: editRole
      });
      
      alert('User updated successfully!');
      setShowEditModal(false);
      await loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update user';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleAddAttendee = async () => {
    if (!newAttendeeName || !newAttendeeEmail || !newAttendeePhone) {
      alert('Please fill in all fields');
      return;
    }

    // Validate phone format (E.164)
    if (!newAttendeePhone.startsWith('+')) {
      alert('Phone number must be in E.164 format (e.g., +15551234567)');
      return;
    }

    setAddingAttendee(true);
    try {
      await eventsAPI.createUser({
        eventId,
        name: newAttendeeName,
        email: newAttendeeEmail,
        phone: newAttendeePhone,
        role: 'attendee'
      });
      
      alert('Attendee added successfully!');
      setShowAddAttendeeModal(false);
      setNewAttendeeName('');
      setNewAttendeeEmail('');
      setNewAttendeePhone('');
      await loadUsers();
    } catch (error) {
      console.error('Error adding attendee:', error);
      alert(error.response?.data?.error || 'Failed to add attendee');
    } finally {
      setAddingAttendee(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) {
      return;
    }
    
    try {
      await eventsAPI.deleteUser(userId);
      alert('User deleted successfully!');
      await loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };
  
  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      alert('Please select users to delete');
      return;
    }
    
    setShowBulkDeleteModal(true);
  };
  
  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      // Delete users one by one
      for (const userId of selectedUsers) {
        await eventsAPI.deleteUser(userId);
      }
      
      alert(`Successfully deleted ${selectedUsers.length} user(s)!`);
      setSelectedUsers([]);
      setShowBulkDeleteModal(false);
      await loadUsers();
    } catch (error) {
      console.error('Error bulk deleting users:', error);
      alert('Failed to delete some users');
    } finally {
      setBulkDeleting(false);
    }
  };
  
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('csv', file);
    
    try {
      const response = await eventsAPI.importUsersCSV(eventId, formData);
      const results = response.data.results;
      alert(`CSV imported!\nCreated: ${results.created}\nUpdated: ${results.updated}\nFailed: ${results.failed}`);
      await loadUsers();
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Failed to upload CSV: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await eventsAPI.exportUsersCSV(eventId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event?.name || 'event'}-users.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV');
    }
  };

  const photographerCount = users.filter(u => u.role === 'photographer').length;
  const attendeeCount = users.filter(u => u.role === 'attendee').length;
  const pendingCount = users.filter(u => u.photographer_requested && u.role === 'attendee').length;

  const handleApprovePhotographer = async (userId, userName) => {
    if (!window.confirm(`Approve ${userName} as a photographer?`)) {
      return;
    }
    
    try {
      await eventsAPI.approvePhotographer(userId);
      alert('Photographer approved!');
      await loadUsers();
    } catch (error) {
      console.error('Error approving photographer:', error);
      alert('Failed to approve photographer');
    }
  };

  const handleRejectPhotographer = async (userId, userName) => {
    if (!window.confirm(`Reject ${userName}'s photographer request?`)) {
      return;
    }
    
    try {
      // Just remove the photographer_requested flag
      await eventsAPI.updateUser(userId, { photographer_requested: false });
      alert('Request rejected');
      await loadUsers();
    } catch (error) {
      console.error('Error rejecting photographer:', error);
      alert('Failed to reject request');
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      alert('Please enter a message');
      return;
    }

    setSendingMessage(true);
    try {
      if (messageRecipient?.name) {
        // Send to individual user (photographer or attendee)
        await eventsAPI.sendUserNotification(messageRecipient.id, messageText);
        alert(`Message sent to ${messageRecipient.name}!`);
      } else if (messageRecipient?.role) {
        // Broadcast to specific role
        await eventsAPI.broadcastNotification(eventId, messageText, messageRecipient.role);
        const roleName = messageRecipient.role === 'attendee' ? 'attendees' : 'photographers';
        alert(`Message sent to all ${roleName}!`);
      } else {
        // Broadcast to all
        await eventsAPI.broadcastNotification(eventId, messageText, 'all');
        alert('Message sent to everyone!');
      }
      setShowMessageModal(false);
      setMessageText('');
      setMessageRecipient(null);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendGalleryLink = async (userId, userName) => {
    if (!window.confirm(`Send gallery link email to ${userName}?`)) {
      return;
    }
    
    setSendingGalleryLink(prev => ({ ...prev, [userId]: true }));
    try {
      // Try sessionStorage first, then localStorage
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Not authenticated. Please log in again.');
      }
      
      const response = await fetch(`https://api.eventpiclive.com/api/events/${eventId}/users/${userId}/send-gallery-link`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send gallery link');
      }
      
      const data = await response.json();
      alert(`Gallery link sent successfully to ${data.email}!`);
    } catch (error) {
      console.error('Error sending gallery link:', error);
      alert(error.message || 'Failed to send gallery link');
    } finally {
      setSendingGalleryLink(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleSendFullGallery = async (userId, userName) => {
    if (!window.confirm(`Send FULL event gallery link to ${userName}?`)) {
      return;
    }
    
    setSendingFullGallery(prev => ({ ...prev, [userId]: true }));
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Not authenticated. Please log in again.');
      }
      
      const response = await fetch(`https://api.eventpiclive.com/api/events/${eventId}/send-full-gallery`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds: [userId] })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send full gallery link');
      }
      
      const data = await response.json();
      alert(`Full gallery link sent successfully to ${userName}!`);
    } catch (error) {
      console.error('Error sending full gallery link:', error);
      alert(error.message || 'Failed to send full gallery link');
    } finally {
      setSendingFullGallery(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/admin/login');
  };

  const adminData = JSON.parse(localStorage.getItem('adminData') || '{}');
  const isSuperAdmin = adminData?.role === 'super_admin';

  // Check if sidebar is collapsed from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  // Listen for sidebar collapse changes
  useEffect(() => {
    const handleStorageChange = () => {
      setSidebarCollapsed(localStorage.getItem('sidebarCollapsed') === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    // Also check periodically since localStorage events don't fire in same tab
    const interval = setInterval(handleStorageChange, 100);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <ThemeToggle />
      <Sidebar onLogout={handleLogout} isSuperAdmin={isSuperAdmin} />
      
      <div className="events-content">
        <div className="events-top">
          <h2>Manage Users</h2>
          <button className="btn-secondary" onClick={() => navigate(`/events/${eventId}/photos`)}>
            <ArrowLeft size={20} />
            Back to Event
          </button>
        </div>
        {event?.name && <p style={{color: 'var(--text-secondary)', marginBottom: '20px'}}>{event.name}</p>}

        {/* Pending Photographer Requests Banner */}
        {pendingCount > 0 && (
          <div style={{
            backgroundColor: '#FEF3C7',
            border: '2px solid #F59E0B',
            borderRadius: '12px',
            padding: '16px 20px',
            margin: '20px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Clock size={24} style={{ color: '#D97706' }} />
              <div>
                <h3 style={{ margin: 0, color: '#92400E', fontSize: '16px', fontWeight: '600' }}>
                  {pendingCount} Pending Photographer Request{pendingCount > 1 ? 's' : ''}
                </h3>
                <p style={{ margin: '4px 0 0 0', color: '#92400E', fontSize: '14px' }}>
                  Review and approve photographer access requests below
                </p>
              </div>
            </div>
            <button 
              className="btn-primary"
              onClick={() => setRoleFilter('pending')}
              style={{ backgroundColor: '#F59E0B', whiteSpace: 'nowrap' }}
            >
              View Requests
            </button>
          </div>
        )}

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        
        <button 
          className={`btn-secondary ${roleFilter === 'all' ? 'active' : ''}`}
          onClick={() => setRoleFilter('all')}
        >
          All ({users.length})
        </button>
        <button 
          className={`btn-secondary ${roleFilter === 'photographer' ? 'active' : ''}`}
          onClick={() => setRoleFilter('photographer')}
        >
          <Camera size={16} />
          Photographers ({photographerCount})
        </button>
        <button 
          className={`btn-secondary ${roleFilter === 'attendee' ? 'active' : ''}`}
          onClick={() => setRoleFilter('attendee')}
        >
          <User size={16} />
          Attendees ({attendeeCount})
        </button>
        {pendingCount > 0 && (
          <button 
            className={`btn-secondary ${roleFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setRoleFilter('pending')}
          >
            <Clock size={14} style={{display: 'inline', marginRight: '4px'}} /> Pending ({pendingCount})
          </button>
        )}
        
        <button 
          className={`btn-secondary ${viewMode === 'card' ? 'active' : ''}`}
          onClick={() => setViewMode('card')}
          title="Card View"
        >
          <Grid size={18} />
        </button>
        <button 
          className={`btn-secondary ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
          title="List View"
        >
          <List size={18} />
        </button>
        
        {selectedUsers.length > 0 && (
          <button 
            className="btn-danger" 
            onClick={handleBulkDelete}
            style={{ backgroundColor: '#ef4444', color: 'white' }}
          >
            <Trash2 size={18} />
            Delete Selected ({selectedUsers.length})
          </button>
        )}
        
        <button className="btn-primary" onClick={() => setShowAddAttendeeModal(true)}>
          <User size={18} />
          Add Attendee
        </button>
        
            <button
          className="btn-primary"
          onClick={() => {
            setMessageRecipient(null);
            setShowMessageModal(true);
          }}
          style={{display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#8B5CF6'}}
        >
          <MessageCircle size={18} />
          Send Message
        </button>
        
        <button
          className="btn-primary"
          onClick={() => setShowSendGalleryModal(true)}
          style={{display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#10B981'}}
        >
          <Send size={18} />
          Send Gallery
        </button>
        
        <label className="btn-secondary" style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
          <Upload size={18} />
          Upload CSV
          <input type="file" accept=".csv" onChange={handleCSVUpload} style={{ display: 'none' }} />
        </label>
        <button className="btn-secondary" onClick={handleExportCSV}>
          <Download size={18} />
          Export CSV
        </button>
      </div>

      <div className="events-grid" style={{display: viewMode === 'list' ? 'block' : 'grid'}}>
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No users found</p>
            <small>Try adjusting your filters or upload a CSV to add users</small>
          </div>
        ) : viewMode === 'card' ? (
          filteredUsers.map(user => (
            <div key={user.id} className="event-card">
                <div className="user-info">
                  <div className="user-header">
                    <h3>{user.name}</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className={`role-badge ${user.role}`}>
                        {user.role === 'photographer' ? <><Camera size={14} style={{display: 'inline', marginRight: '4px'}} /> Photographer</> : <><User size={14} style={{display: 'inline', marginRight: '4px'}} /> Attendee</>}
                      </span>
                      {user.photographer_requested && user.role === 'attendee' && (
                        <span className="role-badge" style={{ backgroundColor: '#F59E0B' }}>
                          <Clock size={14} style={{display: 'inline', marginRight: '4px'}} /> Pending Approval
                        </span>
                      )}
                      {user.role === 'photographer' && user.is_blocked && (
                        <span className="role-badge" style={{ backgroundColor: '#EF4444', fontSize: '11px', padding: '4px 8px' }}>
                          🚫 Blocked
                        </span>
                      )}
                      {user.role === 'photographer' && !user.is_blocked && user.rejection_count > 0 && (
                        <span className="role-badge" style={{ 
                          backgroundColor: user.rejection_count >= 2 ? '#F59E0B' : '#FCD34D',
                          color: '#78350F',
                          fontSize: '11px'
                        }}>
                          ⚠️ {user.rejection_count}/3 strikes
                        </span>
                      )}
                    </div>
                  </div>
                  {user.email && <p className="user-detail">📧 {user.email}</p>}
                  {user.phone && <p className="user-detail">📱 {user.phone}</p>}
                </div>
                <div className="user-actions">
                  {user.photographer_requested && user.role === 'attendee' ? (
                    <>
                      <button className="btn-primary" style={{ backgroundColor: '#10B981', padding: '8px 16px', fontSize: '14px' }} onClick={() => handleApprovePhotographer(user.id, user.name)}>
                        ✓ Approve
                      </button>
                      <button className="btn-secondary" style={{ padding: '8px 16px', fontSize: '14px' }} onClick={() => handleRejectPhotographer(user.id, user.name)}>
                        ✕ Reject
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        className="btn-primary" 
                        style={{padding: '8px 16px', backgroundColor: '#10B981', display: 'flex', alignItems: 'center', gap: '6px'}} 
                        onClick={() => {
                          setRowGalleryUser(user);
                          setShowRowGalleryModal(true);
                        }}
                      >
                        <Send size={14} />
                        Send Gallery
                      </button>
                      <button 
                        className="btn-secondary" 
                        style={{padding: '8px', backgroundColor: '#8B5CF6'}} 
                        onClick={() => {
                          setMessageRecipient(user);
                          setShowMessageModal(true);
                        }}
                        title="Send Message"
                      >
                        <MessageCircle size={16} />
                      </button>
                      <button className="btn-secondary" style={{padding: '8px'}} onClick={() => handleEditUser(user)} title="Edit User">
                        <Edit2 size={16} />
                      </button>
                      <button className="btn-delete" style={{padding: '8px'}} onClick={() => handleDeleteUser(user.id, user.name)} title="Delete User">
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
            </div>
          ))
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{width: '40px'}}>
                    <input 
                      type="checkbox" 
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={toggleSelectAll}
                      style={{cursor: 'pointer'}}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td>
                      <input 
                        type="checkbox" 
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        style={{cursor: 'pointer'}}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {user.name}
                        {user.photographer_requested && user.role === 'attendee' && (
                          <span className="role-badge" style={{ backgroundColor: '#F59E0B', fontSize: '10px', padding: '2px 6px' }}>
                            <Clock size={14} style={{display: 'inline', marginRight: '4px'}} /> Pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{user.email || '-'}</td>
                    <td>{user.phone || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className={`role-badge ${user.role}`}>
                          {user.role === 'photographer' ? <><Camera size={14} style={{display: 'inline', marginRight: '4px'}} /> Photographer</> : <><User size={14} style={{display: 'inline', marginRight: '4px'}} /> Attendee</>}
                        </span>
                        {user.role === 'photographer' && user.is_blocked && (
                          <span className="role-badge" style={{ backgroundColor: '#EF4444', fontSize: '10px', padding: '3px 6px' }}>
                            🚫 Blocked
                          </span>
                        )}
                        {user.role === 'photographer' && !user.is_blocked && user.rejection_count > 0 && (
                          <span className="role-badge" style={{ 
                            backgroundColor: user.rejection_count >= 2 ? '#F59E0B' : '#FCD34D',
                            color: '#78350F',
                            fontSize: '10px',
                            padding: '2px 6px'
                          }}>
                            ⚠️ {user.rejection_count}/3
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {user.photographer_requested && user.role === 'attendee' ? (
                          <>
                            <button className="btn-primary" style={{ backgroundColor: '#10B981', padding: '6px 12px', fontSize: '13px' }} onClick={() => handleApprovePhotographer(user.id, user.name)}>
                              ✓ Approve
                            </button>
                            <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => handleRejectPhotographer(user.id, user.name)}>
                              ✕ Reject
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="btn-primary" 
                              style={{ padding: '8px 16px', backgroundColor: '#10B981', display: 'flex', alignItems: 'center', gap: '6px' }} 
                              onClick={() => {
                                setRowGalleryUser(user);
                                setShowRowGalleryModal(true);
                              }}
                            >
                              <Send size={14} />
                              Send Gallery
                            </button>
                            <button 
                              className="btn-secondary" 
                              style={{ padding: '8px', backgroundColor: '#8B5CF6' }} 
                              onClick={() => {
                                setMessageRecipient(user);
                                setShowMessageModal(true);
                              }}
                              title="Send Message"
                            >
                              <MessageCircle size={16} />
                            </button>
                            <button className="btn-secondary" style={{ padding: '8px' }} onClick={() => handleEditUser(user)} title="Edit User">
                              <Edit2 size={16} />
                            </button>
                            <button className="btn-delete" style={{ padding: '8px' }} onClick={() => handleDeleteUser(user.id, user.name)} title="Delete User">
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit User</h2>
            
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Name"
              />
            </div>
            
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="Email"
              />
            </div>
            
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                placeholder="Phone"
              />
            </div>
            
            <div className="form-group">
              <label>Role</label>
              <div className="role-selector">
                <button 
                  className={editRole === 'attendee' ? 'active' : ''}
                  onClick={() => setEditRole('attendee')}
                >
                  <User size={18} />
                  Attendee
                </button>
                <button 
                  className={editRole === 'photographer' ? 'active' : ''}
                  onClick={() => setEditRole('photographer')}
                >
                  <Camera size={18} />
                  Photographer
                </button>
              </div>
            </div>
            
            {/* Photographer Strikes & Blocking */}
            {editingUser && editingUser.role === 'photographer' && (
              <div className="form-group" style={{ 
                marginTop: '20px', 
                padding: '16px', 
                backgroundColor: editingUser.is_blocked ? '#FEF2F2' : '#F9FAFB',
                borderRadius: '8px',
                border: editingUser.is_blocked ? '2px solid #EF4444' : '1px solid #E5E7EB'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  color: editingUser.is_blocked ? '#DC2626' : '#374151',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  {editingUser.is_blocked ? '🚫' : '⚠️'} Moderation Status
                </label>
                
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#6B7280',
                    marginBottom: '8px'
                  }}>
                    Rejection Count: <strong style={{ 
                      color: editingUser.is_blocked ? '#DC2626' : (editingUser.rejection_count >= 2 ? '#F59E0B' : '#10B981')
                    }}>{editingUser.rejection_count || 0}/3</strong>
                  </div>
                  
                  {editingUser.is_blocked && (
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#DC2626',
                      backgroundColor: '#FEE2E2',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      marginBottom: '12px'
                    }}>
                      ⛔ This photographer is blocked from uploading photos
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  {editingUser.is_blocked ? (
                    <button 
                      className="btn-primary"
                      onClick={async () => {
                        try {
                          await eventsAPI.unblockPhotographer(editingUser.id);
                          alert('Photographer unblocked successfully!');
                          await loadUsers();
                          setShowEditModal(false);
                        } catch (err) {
                          console.error('Error unblocking:', err);
                          alert('Failed to unblock photographer');
                        }
                      }}
                      style={{ 
                        backgroundColor: '#10B981',
                        flex: 1
                      }}
                    >
                      ✓ Unblock Photographer
                    </button>
                  ) : (
                    <button 
                      className="btn-delete"
                      onClick={async () => {
                        if (!window.confirm(`Block ${editingUser.name}? They will not be able to upload photos.`)) return;
                        try {
                          await eventsAPI.blockPhotographer(editingUser.id);
                          alert('Photographer blocked successfully!');
                          await loadUsers();
                          setShowEditModal(false);
                        } catch (err) {
                          console.error('Error blocking:', err);
                          alert('Failed to block photographer');
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      🚫 Block Photographer
                    </button>
                  )}
                  
                  {editingUser.rejection_count > 0 && (
                    <button 
                      className="btn-secondary"
                      onClick={async () => {
                        if (!window.confirm(`Reset strikes for ${editingUser.name}?`)) return;
                        try {
                          await eventsAPI.resetPhotographerStrikes(editingUser.id);
                          alert('Strikes reset successfully!');
                          await loadUsers();
                          setShowEditModal(false);
                        } catch (err) {
                          console.error('Error resetting strikes:', err);
                          alert('Failed to reset strikes');
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      🔄 Reset Strikes
                    </button>
                  )}
                </div>
                
                {/* Biometric Data Management */}
                <div style={{ marginTop: '20px', padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    🔒 Biometric Data Management
                  </h4>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                    Delete this user's face recognition data and selfie photos. Contact info will be preserved.
                  </p>
                  <button 
                    className="btn-danger"
                    onClick={async () => {
                      if (!window.confirm(`Delete all biometric data for ${editingUser.name}?\n\nThis will:\n- Remove face from AWS Rekognition\n- Delete selfie photos\n- Keep contact info (name, email, phone)\n\nThis action cannot be undone.`)) return;
                      try {
                        const response = await eventsAPI.deleteUserBiometricData(editingUser.id);
                        alert(`Biometric data deleted successfully!\n\nDeleted:\n- ${response.data.stats.facesDeleted} faces from Rekognition\n- ${response.data.stats.selfiesDeleted} selfie photos`);
                        await loadUsers();
                        setShowEditModal(false);
                      } catch (err) {
                        console.error('Error deleting biometric data:', err);
                        alert('Failed to delete biometric data');
                      }
                    }}
                    style={{ width: '100%' }}
                  >
                    🗑️ Delete Biometric Data
                  </button>
                </div>
              </div>
            )}
            
            <div className="modal-actions" style={{display: 'flex', gap: '12px', marginTop: '24px'}}>
              <button className="btn-primary" onClick={handleSaveUser} disabled={saving} style={{flex: 1}}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="btn-secondary" onClick={() => setShowEditModal(false)} style={{flex: 1}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Attendee Modal */}
      {showAddAttendeeModal && (
        <div className="modal-overlay" onClick={() => setShowAddAttendeeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Attendee</h2>
            
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={newAttendeeName}
                onChange={(e) => setNewAttendeeName(e.target.value)}
                placeholder="Enter attendee name"
              />
            </div>
            
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={newAttendeeEmail}
                onChange={(e) => setNewAttendeeEmail(e.target.value)}
                placeholder="Enter email address"
              />
            </div>
            
            <div className="form-group">
              <label>Phone Number * (E.164 format)</label>
              <input
                type="tel"
                value={newAttendeePhone}
                onChange={(e) => setNewAttendeePhone(e.target.value)}
                placeholder="+15551234567"
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Must start with + followed by country code and number
              </small>
            </div>
            
            <div className="modal-actions" style={{display: 'flex', gap: '12px', marginTop: '24px'}}>
              <button className="btn-primary" onClick={handleAddAttendee} disabled={addingAttendee} style={{flex: 1}}>
                {addingAttendee ? 'Adding...' : 'Add Attendee'}
              </button>
              <button className="btn-secondary" onClick={() => setShowAddAttendeeModal(false)} style={{flex: 1}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      {/* Message Modal */}
      {showMessageModal && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '600px'}}>
            <h3 style={{marginBottom: '16px', fontSize: '20px'}}>Send Message</h3>
            
            {!messageRecipient?.name && (
              <div className="form-group">
                <label>Send To</label>
                <select
                  value={
                    messageRecipient?.role === 'photographer' ? 'photographers' :
                    messageRecipient?.role === 'attendee' ? 'attendees' :
                    'all'
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'all') {
                      setMessageRecipient(null);
                    } else if (value === 'photographers') {
                      setMessageRecipient({ role: 'photographer' });
                    } else if (value === 'attendees') {
                      setMessageRecipient({ role: 'attendee' });
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px'
                  }}
                >
                  <option value="all">Everyone ({users.length} users)</option>
                  <option value="photographers">All Photographers ({users.filter(u => u.role === 'photographer').length})</option>
                  <option value="attendees">All Attendees ({users.filter(u => u.role === 'attendee').length})</option>
                </select>
              </div>
            )}
            
            {messageRecipient?.name && (
              <div style={{
                backgroundColor: '#EFF6FF',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <User size={16} style={{color: '#3B82F6'}} />
                <span style={{color: '#1E40AF', fontSize: '14px'}}>
                  Sending to: {messageRecipient.name}
                </span>
              </div>
            )}
            
            <div className="form-group">
              <label>Message</label>
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type your announcement here..."
                rows={6}
                style={{width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #D1D5DB'}}
              />
            </div>
            
            <div className="modal-actions" style={{display: 'flex', gap: '12px', marginTop: '24px'}}>
              <button 
                className="btn-primary" 
                onClick={handleSendMessage} 
                disabled={sendingMessage || !messageText.trim()} 
                style={{flex: 1, backgroundColor: '#8B5CF6'}}
              >
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => {
                  setShowMessageModal(false);
                  setMessageText('');
                  setMessageRecipient(null);
                }} 
                style={{flex: 1}}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Send Gallery Modal (Top Button) */}
      {showSendGalleryModal && (
        <div className="modal-overlay" onClick={() => setShowSendGalleryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '600px'}}>
            <h3 style={{marginBottom: '20px', fontSize: '20px'}}>Send Gallery Link</h3>
            
            {/* Gallery Type Selection */}
            <div className="form-group" style={{marginBottom: '20px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Gallery Type</label>
              <div style={{display: 'flex', gap: '12px'}}>
                <button
                  onClick={() => setSendGalleryType('personal')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: sendGalleryType === 'personal' ? '2px solid #3B82F6' : '1px solid #D1D5DB',
                    backgroundColor: sendGalleryType === 'personal' ? '#EFF6FF' : 'white',
                    color: sendGalleryType === 'personal' ? '#1E40AF' : '#6B7280',
                    cursor: 'pointer',
                    fontWeight: sendGalleryType === 'personal' ? '600' : '400'
                  }}
                >
                  Personal Gallery
                </button>
                <button
                  onClick={() => setSendGalleryType('full')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: sendGalleryType === 'full' ? '2px solid #10B981' : '1px solid #D1D5DB',
                    backgroundColor: sendGalleryType === 'full' ? '#ECFDF5' : 'white',
                    color: sendGalleryType === 'full' ? '#065F46' : '#6B7280',
                    cursor: 'pointer',
                    fontWeight: sendGalleryType === 'full' ? '600' : '400'
                  }}
                >
                  Full Gallery
                </button>
              </div>
            </div>
            
            {/* Attendee Selection */}
            <div className="form-group" style={{marginBottom: '20px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Send To</label>
              <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #D1D5DB', borderRadius: '8px', padding: '12px'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={selectedAttendees.length === users.filter(u => u.role === 'attendee').length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAttendees(users.filter(u => u.role === 'attendee').map(u => u.id));
                      } else {
                        setSelectedAttendees([]);
                      }
                    }}
                  />
                  <strong>Select All ({users.filter(u => u.role === 'attendee').length} attendees)</strong>
                </label>
                <hr style={{margin: '8px 0', border: 'none', borderTop: '1px solid #E5E7EB'}} />
                {users.filter(u => u.role === 'attendee').map(user => (
                  <label key={user.id} style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', cursor: 'pointer', borderRadius: '4px'}} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <input
                      type="checkbox"
                      checked={selectedAttendees.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAttendees([...selectedAttendees, user.id]);
                        } else {
                          setSelectedAttendees(selectedAttendees.filter(id => id !== user.id));
                        }
                      }}
                    />
                    <span>{user.name}</span>
                    {user.email && <span style={{color: '#6B7280', fontSize: '12px'}}>({user.email})</span>}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="modal-actions" style={{display: 'flex', gap: '12px', marginTop: '24px'}}>
              <button 
                className="btn-primary" 
                onClick={async () => {
                  if (selectedAttendees.length === 0) {
                    alert('Please select at least one attendee');
                    return;
                  }
                  
                  setSendingGallery(true);
                  try {
                    const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
                    if (!token) throw new Error('Not authenticated');
                    
                    const endpoint = sendGalleryType === 'personal' 
                      ? `/api/events/${eventId}/send-gallery-link`
                      : `/api/events/${eventId}/send-full-gallery`;
                    
                    const response = await fetch(`https://api.eventpiclive.com${endpoint}`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ userIds: selectedAttendees })
                    });
                    
                    if (!response.ok) {
                      const error = await response.json();
                      throw new Error(error.error || 'Failed to send gallery links');
                    }
                    
                    alert(`${sendGalleryType === 'personal' ? 'Personal' : 'Full'} gallery links sent to ${selectedAttendees.length} attendee(s)!`);
                    setShowSendGalleryModal(false);
                    setSelectedAttendees([]);
                    setSendGalleryType('personal');
                  } catch (error) {
                    console.error('Error sending gallery:', error);
                    alert(error.message || 'Failed to send gallery links');
                  } finally {
                    setSendingGallery(false);
                  }
                }}
                disabled={sendingGallery || selectedAttendees.length === 0}
                style={{flex: 1, backgroundColor: '#10B981'}}
              >
                {sendingGallery ? 'Sending...' : `Send to ${selectedAttendees.length} attendee(s)`}
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => {
                  setShowSendGalleryModal(false);
                  setSelectedAttendees([]);
                  setSendGalleryType('personal');
                }} 
                style={{flex: 1}}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Send Gallery Modal (Row Button) */}
      {showRowGalleryModal && rowGalleryUser && (
        <div className="modal-overlay" onClick={() => setShowRowGalleryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
            <h3 style={{marginBottom: '16px', fontSize: '20px'}}>Send Gallery to {rowGalleryUser.name}</h3>
            
            {/* Gallery Type Selection */}
            <div className="form-group" style={{marginBottom: '20px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Gallery Type</label>
              <div style={{display: 'flex', gap: '12px'}}>
                <button
                  onClick={() => setRowGalleryType('personal')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: rowGalleryType === 'personal' ? '2px solid #3B82F6' : '1px solid #D1D5DB',
                    backgroundColor: rowGalleryType === 'personal' ? '#EFF6FF' : 'white',
                    color: rowGalleryType === 'personal' ? '#1E40AF' : '#6B7280',
                    cursor: 'pointer',
                    fontWeight: rowGalleryType === 'personal' ? '600' : '400'
                  }}
                >
                  Personal Gallery
                </button>
                <button
                  onClick={() => setRowGalleryType('full')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: rowGalleryType === 'full' ? '2px solid #10B981' : '1px solid #D1D5DB',
                    backgroundColor: rowGalleryType === 'full' ? '#ECFDF5' : 'white',
                    color: rowGalleryType === 'full' ? '#065F46' : '#6B7280',
                    cursor: 'pointer',
                    fontWeight: rowGalleryType === 'full' ? '600' : '400'
                  }}
                >
                  Full Gallery
                </button>
              </div>
            </div>
            
            <div className="modal-actions" style={{display: 'flex', gap: '12px', marginTop: '24px'}}>
              <button 
                className="btn-primary" 
                onClick={async () => {
                  setSendingGallery(true);
                  try {
                    const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
                    if (!token) throw new Error('Not authenticated');
                    
                    const endpoint = rowGalleryType === 'personal' 
                      ? `/api/events/${eventId}/send-gallery-link`
                      : `/api/events/${eventId}/send-full-gallery`;
                    
                    const response = await fetch(`https://api.eventpiclive.com${endpoint}`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ userIds: [rowGalleryUser.id] })
                    });
                    
                    if (!response.ok) {
                      const error = await response.json();
                      throw new Error(error.error || 'Failed to send gallery link');
                    }
                    
                    alert(`${rowGalleryType === 'personal' ? 'Personal' : 'Full'} gallery link sent to ${rowGalleryUser.name}!`);
                    setShowRowGalleryModal(false);
                    setRowGalleryUser(null);
                    setRowGalleryType('personal');
                  } catch (error) {
                    console.error('Error sending gallery:', error);
                    alert(error.message || 'Failed to send gallery link');
                  } finally {
                    setSendingGallery(false);
                  }
                }}
                disabled={sendingGallery}
                style={{flex: 1, backgroundColor: '#10B981'}}
              >
                {sendingGallery ? 'Sending...' : 'Send Gallery'}
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => {
                  setShowRowGalleryModal(false);
                  setRowGalleryUser(null);
                  setRowGalleryType('personal');
                }} 
                style={{flex: 1}}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowBulkDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
            <h3 style={{marginBottom: '20px', fontSize: '20px', color: '#ef4444'}}>
              <Trash2 size={24} style={{display: 'inline', marginRight: '8px', verticalAlign: 'middle'}} />
              Confirm Bulk Delete
            </h3>
            
            <p style={{marginBottom: '20px', fontSize: '16px'}}>
              Are you sure you want to delete <strong>{selectedUsers.length}</strong> user(s)?
            </p>
            
            <p style={{marginBottom: '20px', fontSize: '14px', color: 'var(--text-secondary)'}}>
              This action cannot be undone. All user data, photos, and face recognition data will be permanently deleted.
            </p>
            
            <div className="modal-actions">
              <button 
                className="btn-danger" 
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                style={{backgroundColor: '#ef4444', color: 'white'}}
              >
                {bulkDeleting ? 'Deleting...' : `Delete ${selectedUsers.length} User(s)`}
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={bulkDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUsers;
